import React, {
  ReactElement,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useQueryClient } from 'react-query';
import { Box, Flex, Text } from 'rebass/styled-components';
import { SpaceProps, LayoutProps } from 'styled-system';
import { Tooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import { useHistory, useParams } from 'react-router-dom';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';

import ProceduresLists from './ProcedureList';
import SelectedProceduresList from './SelectedProceduresList';
import EmbeddedProcedure from './EmbeddedProcedure';
import ExistingLayout from './ExistingLayout';
import ExistingTable from './ExistingTable';
import FileImage from './FileImage';
import Hyperlink from './Hyperlink';
import SaveLayout from './SaveLayout';
import SaveTable from './SaveTable';
import ImportProcedure from './ImportProcedure';
import LinkProcedure from './LinkProcedure';
import VersionHistory from './VersionHistory';
import FileBrowser from './FileBrowser';
import BrokenLinks from './BrokenLinks';
import useProcedureBuilder from './useProcedureBuilder';

import {
  MainContent,
  PageHeader,
  Card,
  CardHeader,
  ButtonGroup,
  Loader,
  EmptyState,
  Alert,
  Dropdown,
} from 'ui/patterns';
import {
  Button,
  Checkbox,
  Iframe,
  Stack,
  IframeWrapper,
  Badge,
  Spinner,
} from 'ui/components';
import { TinyMCE } from 'ui/components/tinyMCE/ProcedureEditor';

import usePrompt from 'hooks/usePrompt';
import {
  useDocumentTitle,
  useResetPolicyMenu,
  useCreatePolicyMenu,
} from 'hooks';

import {
  ProcedureEditActions,
  LastEdited,
  ProcedureEditorWrapper,
  InfoText,
  VersionButton,
} from './styles';
import * as Icon from 'assets/icons';
import NoDataImage from 'assets/images/NoDataImage';

import {
  BlobType,
  ProcedureBuilderListType,
  ProcedureCategoryType,
  ProcedureReturnType,
} from 'types/procedureBuilder';

import { showSuccessToast, showErrorToast } from 'utils/showToast';
import { lazyRun } from 'utils/useInterval';
import { confirmAlert } from 'utils/confirm-alert';
import {
  checkIfIframe,
  addParamsToIframeSrc,
} from 'utils/procedureBuilderHelpers';

import { ResultModal, ErrorModal } from 'types/modalTypes';
import { useApplicationThemeStore } from '../../store/useApplicationThemeStore';
import { checkAzureFileNameValidation } from 'utils/fileUtils';
import { useFetchProcedureCategories } from 'hooks/useFetchProcedureCategory';
import { useUpdateProcedureCategory } from 'hooks/useUpdateProcedureCategory';

dayjs.extend(utc);
type StyleProps = SpaceProps & LayoutProps;
type ProcedureBuilder = StyleProps;

export default function ProcedureBuilder({
  ...StyleProps
}: ProcedureBuilder): ReactElement {
  const [mode, setMode] = useState<'Edit' | 'Preview'>('Edit');
  const [importProcedureVisible, setImportProcedureVisible] =
    useState<boolean>(false);
  const [linkProcedureVisible, setLinkProcedureVisible] =
    useState<boolean>(false);
  const [versionHistoryVisible, setVersionHistoryVisible] =
    useState<boolean>(false);
  const [fileBrowserVisible, setFileBrowserVisible] = useState<boolean>(false);
  const [showEmbeddedProcedure, setShowEmbeddedProcedure] = useState(false);
  const [showExistingLayout, setShowExistingLayout] = useState(false);
  const [showExistingTable, setShowExistingTable] = useState(false);

  const [showFileImage, setShowFileImage] = useState(false);
  const [iframeLoader, setIframeLoader] = useState(false);
  const [showHyperlink, setShowHyperlink] = useState<string>('');
  const [showSaveLayout, setShowSaveLayout] = useState<string | undefined>();
  const [showSaveTable, setShowSaveTable] = useState<string | undefined>();
  const [isTableSelected, setIsTableSelected] = useState<boolean>(false);
  const [random, setRandom] = useState(0);
  const [renameFailed, setRenameFailed] = useState(false);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  // const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
  //   null,
  // );

  const history = useHistory<{ ID: number; Pid?: number }>();
  const params = useParams<{
    accountId: string;
    tabId?: string;
  }>();
  const accountId = parseInt(params.accountId);
  const pid = history?.location?.state?.Pid;
  const queryClient = useQueryClient();

  const {
    brokenLinks,
    accountInfo,
    procedures,
    procedureDetails,
    selectedProcedures,
    isLoading,
    searchValue,
    renameProc,
    totalBrokenLinksCount,
    procedureList,
    updateProcedureVisibility,
    setSelectedProcedures,
    getProcedureReference,
    undoProcedureContent,
    updateProcedureAutoSaveOption,
    renameProcedure,
    setProcedures,
    setSearchValue,
    setRenameProc,
    saveContent,
    pageActions,
    selectProcedure,
    createProcedure,
    appliedFilterOption,
    setAppliedFilterOption,
    setUserTyped,
    editorChanges,
    setEditorChanges,
    userTyped,
    content,
    setContent,
    saveTemplate,
    updateTemplate,
    createFolder,
    uploadFile,
    deleteFile,
    showBrokenLinks,
    toggleBrokenLinks,
    autoSaveContent,
    autoPublishPolicyContent,
    isSaveProcedureLoading,
    formatCleanProcedure,
    forceRenameProcedure,
    refetchProcedureDetails,
  } = useProcedureBuilder({
    accountId,
    pid,
    mode,
  });

  const colorScheme = useApplicationThemeStore(
    React.useCallback(state => state.colourScheme, []),
  );

  const tempContent = useApplicationThemeStore(
    React.useCallback(state => state.tempContent, []),
  );

  const { data: procedureCategories } = useFetchProcedureCategories();

  const categoryMenuList = useMemo(() => {
    return procedureCategories?.Data?.map(
      (category: ProcedureCategoryType, index: number): any => ({
        id: category.Id ?? index,
        value: category.Label,
        colorCode: category.HexCode,
        pattern: category.Pattern,
      }),
    );
  }, [procedureCategories?.Data]);

  const { mutate: resetPolicyProcedure, isLoading: isPolicyMenuResetting } =
    useResetPolicyMenu();

  const { mutate: createPolicyMenu, isLoading: isPolicyMenuCreating } =
    useCreatePolicyMenu();

  const {
    mutate: updateProcedureCategory,
    isLoading: isProcedureCategoryUpdateInProgress,
  } = useUpdateProcedureCategory();

  const apiBaseUrl = process.env.REACT_APP_API_END_POINT ?? '';
  const oldUrl = process.env.REACT_APP_LEGACY_API_END_POINT ?? '';

  // Extract & rewrite file links inside HTML content
  const transformFileLinks = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('a[href]').forEach(a => {
      const href = a.getAttribute('href') ?? '';
      // Skip links that don't start with oldUrl
      if (!href.startsWith(oldUrl)) return;

      // Extract the filename (last segment)
      let fileName = href.split('/').filter(Boolean).pop() ?? '';

      if (fileName.endsWith('/')) {
        fileName = fileName.slice(0, -1);
      }
      const newHref = `${apiBaseUrl}accounts/procedures/get-external-file?filepath=root/SharedFiles/${fileName}`;
      a.setAttribute('href', newHref);
      a.setAttribute('title', newHref);
      a.textContent = fileName;
    });

    return doc.body.innerHTML; // cleaner than outerHTML
  };

  useEffect(() => {
    if (isSavingLayout) return;

    if (procedureDetails?.Data?.Content) {
      const isIframePresent = checkIfIframe(procedureDetails?.Data?.Content);

      let tempContent = procedureDetails?.Data?.Content;

      if (isIframePresent) {
        tempContent = addParamsToIframeSrc(tempContent);
      }
      tempContent = transformFileLinks(tempContent);

      setContent(tempContent);
    } else {
      setContent('');
    }
  }, [procedureDetails?.Data?.Content, setContent]);

  useEffect(() => {
    if (mode === 'Preview') setMode('Edit');
  }, [procedureDetails]);

  useEffect(() => {
    if (accountId === undefined) {
      history.push('/home');
    }
  }, [accountId, history]);

  useEffect(() => {
    const isIframePresent = checkIfIframe(tempContent);
    let contentTemp: string;
    if (isIframePresent === true) {
      contentTemp = addParamsToIframeSrc(tempContent);
    } else contentTemp = tempContent;
    setContent(contentTemp);
  }, [colorScheme]);

  const accountName = `${accountInfo?.accountInfoReturn?.Data.Name}`;
  const accountIdentifier = `${'('} ${
    accountInfo?.accountInfoReturn?.Data.CID
  } ${'-'} ${accountInfo?.accountInfoReturn?.Data.SID} ${'-'} ${
    accountInfo?.accountInfoReturn?.Data.PID
  } ${')'}`;

  function toggleImportProcedureVisible() {
    setImportProcedureVisible(
      importProcedureVisible => !importProcedureVisible,
    );
  }

  function toggleLinkProcedureVisible() {
    setLinkProcedureVisible(linkProcedureVisible => !linkProcedureVisible);
  }

  const lastEditedPerson = useMemo(() => {
    if (procedureDetails?.Data?.AccountProcedure?.Procedure?.RecentVersion)
      return procedureDetails.Data.AccountProcedure.Procedure.RecentVersion
        .CreatedBy;
  }, [procedureDetails?.Data?.AccountProcedure]);

  const lastEditedTime = useMemo(() => {
    if (procedureDetails?.Data?.AccountProcedure.Procedure?.RecentVersion) {
      const date = dayjs
        .utc(
          procedureDetails.Data.AccountProcedure.Procedure.RecentVersion
            .CreatedOn,
        )
        .local()
        .format('MMM  DD, YYYY');
      const time = dayjs
        .utc(
          procedureDetails.Data.AccountProcedure.Procedure?.RecentVersion
            ?.CreatedOn,
        )
        .local()
        .format('h:mm A');
      return {
        date,
        time,
      };
    }
  }, [procedureDetails?.Data?.AccountProcedure]);

  const titleName = () => {
    if (linkProcedureVisible) {
      return 'Link Procedure';
    }
    if (importProcedureVisible) {
      return 'Import Procedure';
    } else {
      return `${accountName} ${accountIdentifier}`;
    }
  };

  const subtitle = titleName();
  useDocumentTitle(subtitle, true);

  function toggleVersionHistory() {
    setVersionHistoryVisible(versionHistoryVisible => !versionHistoryVisible);
    queryClient.invalidateQueries(
      [
        'get_procedure_version_history',
        accountId,
        selectedProcedures && selectedProcedures[0],
      ],
      {
        refetchInactive: true,
      },
    );
  }

  function toggleFileBrowser() {
    setFileBrowserVisible(fileBrowserVisible => !fileBrowserVisible);
  }

  function toggleEmbeddedProcedure(
    params:
      | {
          account: string;
          procedureName: string;
        }
      | undefined,
  ) {
    setShowEmbeddedProcedure(!showEmbeddedProcedure);
    if (params) {
      const iframeContent = `<iframe style="width:100%;height: 300px;"
       src="${apiBaseUrl}procedures/V1/accountfolders/${params.account}/Policies/${params.procedureName}?procedureDisplayType=2" id="${params.procedureName}"></iframe>`;
      (window as any)?.tinymce?.activeEditor?.insertContent(iframeContent);
    }
  }

  function toggleExistingLayout() {
    setShowExistingLayout(!showExistingLayout);
  }

  function toggleExistingTable() {
    setShowExistingTable(!showExistingTable);
  }

  function toggleFileImage() {
    setShowFileImage(showFileImage => !showFileImage);
  }

  function toggleHyperlink() {
    const selectedWords = (
      window as any
    )?.tinymce?.activeEditor?.selection?.getContent({ format: 'text' });
    if (selectedWords?.length) setShowHyperlink(selectedWords);
  }

  function getAllTags(): Array<{ Id: string; html: string }> {
    const tagNames: Array<{ Id: string; html: string }> = [];
    const content = (window as any)?.tinymce?.activeEditor?.getBody();
    if (content) {
      const links = content.querySelectorAll('[id^="hyper_link-"]');
      for (let i = 0; i < links?.length; i++) {
        tagNames.push({
          Id: links[i].getAttribute('Id'),
          html: links[i].dataset.linktext,
        });
      }
    }
    return tagNames;
  }

  function toggleSaveLayout(value?: string) {
    setShowSaveLayout(value);
  }

  function toggleSaveTable(value?: string) {
    setShowSaveTable(value);
  }

  function cancelAutosaveCallbacks() {
    lazyRun(() => {
      //
    }, 10);
  }

  const unsavedChanges = (() => {
    let newContent = '';
    if (
      (window as any)?.tinymce?.activeEditor?.isDirty() &&
      editorChanges === true &&
      procedureDetails &&
      procedureDetails?.Data?.Content
    ) {
      if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
        newContent = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
          procedureDetails?.Data?.AccountProcedure?.AccountId
        }/Policies/'></head><body>${(
          window as any
        )?.tinymce?.activeEditor?.getContent()}</body></html>`;
      }
      if (newContent) {
        return newContent !== procedureDetails?.Data.Content;
      }
    }
    return false;
  })();

  /*********RENAME PROCEDURE******************************************************************/
  //Do not show again checkbox//
  const handleRenameModalVisiblity = (e: any) => {
    if (e.target.checked === true) {
      localStorage.setItem('renameModalConfirmation', JSON.stringify(true));
    } else {
      localStorage.removeItem('renameModalConfirmation');
    }
  };
  //function to handle apis
  const confirmRenameProcedure = ({
    oldProcedureName,
    procedureIds,
    accountId,
    renameProcedureProps,
    cb,
  }: {
    oldProcedureName: string;
    procedureIds: Array<number>;
    accountId: number;
    renameProcedureProps: ProcedureReturnType;
    cb: (isSuccess: boolean) => void;
  }) => {
    getProcedureReference(
      {
        procedureIds: [...procedureIds],
        isRemove: false,
        accountId: accountId,
      },
      {
        onSuccess: (mutationResult: any) => {
          if (mutationResult.Data.length === 0) {
            handleRenameProcedureApi(
              oldProcedureName,
              renameProcedureProps,
              cb,
            );
          } else {
            if (!mutationResult.Data[0].includes('0000_POLICIESMENU.html')) {
              confirmAlert({
                message:
                  'This procedure has been linked to other procedures. Are you sure you want to rename the procedure?',
                buttons: [
                  {
                    label: 'Yes',
                    onClick: () => {
                      handleRenameProcedureApi(
                        oldProcedureName,
                        renameProcedureProps,
                        cb,
                      );
                    },
                    className: 'pr-btn-primary',
                  },
                  {
                    label: 'No',
                    onClick: () => {
                      handleCancelRenameAlert();
                      cb(false);
                    },
                  },
                ],
                onKeypressEscape: () => {
                  handleCancelRenameAlert();
                  cb(false);
                },
                onClickOutside: () => {
                  handleCancelRenameAlert();
                  cb(false);
                },
              });
            } else {
              handleRenameProcedureApi(
                oldProcedureName,
                renameProcedureProps,
                cb,
              );
            }
          }
        },
        onError: (error: ErrorModal) => {
          cb(false);
          showErrorToast({
            message: error.Errors[0].Message,
          });
        },
      },
    );
  };
  // rename procedure function and api
  const handleRenameProcedures = (
    newName: string,
    cb: (isSuccess: boolean) => void,
  ) => {
    let editedName = '';
    if (!newName.endsWith('.html')) {
      if (!renameProc?.ProcedureName.endsWith('.htm')) {
        const extension = `${newName}.html`;
        editedName = newName.replace(newName, extension);
      } else {
        const extension = `${newName}.html`;
        editedName = newName.replace(newName, extension);
      }
    } else {
      editedName = newName;
    }
    if (!renameProc) return;
    const renameProcedureProps: ProcedureReturnType = {
      AccountId: renameProc.AccountId,
      ProcedureId: renameProc.ProcedureId,
      ProcedureName: editedName.trim(),
    };
    const tempValue = localStorage.getItem('renameModalConfirmation');
    const showConfirmation = tempValue === null ? false : JSON.parse(tempValue);
    const procIds: Array<number> = [];
    if (renameProcedureProps) procIds.push(renameProcedureProps.ProcedureId);
    if (!showConfirmation) {
      confirmAlert({
        dontShowAgain:
          renameProc?.ProcedureName === '0000_POLICIESMENU.html' ? false : true,
        handleModalVisibility: handleRenameModalVisiblity,
        message:
          renameProc?.ProcedureName === '0000_POLICIESMENU.html'
            ? 'You are about to rename the Policies Menu. This will automatically create a new Policies Menu using currently visible procedures. Any manual customizations will be lost. Do you want to proceed?'
            : 'This will change the name of the procedure document. Any links to the procedure will need to reflect this change. The Policies Menu will self-update as needed.',
        buttons: [
          {
            label:
              renameProc?.ProcedureName === '0000_POLICIESMENU.html'
                ? 'Yes'
                : 'Ok',
            onClick: () => {
              confirmRenameProcedure({
                oldProcedureName: renameProc.ProcedureName,
                procedureIds: [...procIds],
                accountId: accountId,
                renameProcedureProps,
                cb,
              });
            },
            className: 'pr-btn-primary',
          },
          {
            label:
              renameProc?.ProcedureName === '0000_POLICIESMENU.html'
                ? 'No'
                : 'Cancel',
            onClick: () => {
              handleCancelRenameAlert();
              cb(false);
            },
          },
        ],
        onKeypressEscape: () => {
          handleCancelRenameAlert();
          cb(false);
        },
        onClickOutside: () => {
          handleCancelRenameAlert();
          cb(false);
        },
      });
    } else {
      confirmRenameProcedure({
        oldProcedureName: renameProc.ProcedureName,
        procedureIds: [...procIds],
        accountId: accountId,
        renameProcedureProps,
        cb,
      });
    }
  };
  function handleRenameProcedureApi(
    oldProcedureName: string,
    renameProc: ProcedureReturnType,
    cb: (isSuccess: boolean) => void,
  ) {
    renameProcedure(
      {
        ...renameProc,
      },
      {
        onSuccess: (mutationResult: ResultModal) => {
          if (mutationResult.Data && renameProc) {
            if (oldProcedureName === '0000_POLICIESMENU.html') {
              createPolicyMenu({ accountId });
            }
            cb(true);
            setSearchValue('');
            setRenameProc(undefined);
          }
          showSuccessToast({
            message: 'Your changes were saved successfully.',
          });
        },
        onError: (error: ErrorModal) => {
          queryClient.invalidateQueries(
            ['get_procedure_builder_procedures', renameProc?.AccountId],
            {
              refetchInactive: true,
            },
          );
          queryClient.invalidateQueries(
            ['get_procedure', renameProc?.AccountId, renameProc.ProcedureId],
            {
              refetchInactive: true,
            },
          );
          if (
            error.StatusCode === 409 &&
            error.Errors[0].Message ===
              'The title shown in the Policy Menu was edited. Do you want to update it to match the new procedure file name?'
          ) {
            confirmAlert({
              message: `${error.Errors[0].Message}`,
              buttons: [
                {
                  label: 'Yes',
                  onClick: () => {
                    forceRenameProcedure(
                      {
                        accountId: renameProc.AccountId,
                        procedureId: renameProc.ProcedureId,
                      },
                      {
                        onSuccess: (mutationResult: ResultModal) => {
                          if (mutationResult.Data && renameProc) {
                            setSearchValue('');
                            setRenameProc(undefined);
                          }
                          showSuccessToast({
                            message: 'Your changes were saved successfully.',
                          });
                        },
                        onError: (error: ErrorModal) => {
                          showErrorToast({
                            message: error.Errors[0].Message,
                          });
                        },
                      },
                    );
                  },
                  className: 'pr-btn-primary',
                },
                {
                  label: 'No',
                  onClick: () => {
                    //
                  },
                },
              ],
            });
          } else if (
            error.StatusCode === 404 &&
            error.Errors[0].Message === 'PoliciesMenu Not Found'
          ) {
            showSuccessToast({
              message: 'Your changes were saved successfully',
            });
          } else {
            setRenameFailed(true);
            queryClient.invalidateQueries(
              ['get_procedure', renameProc?.AccountId],
              {
                refetchInactive: true,
              },
            );
            if (error.message)
              showErrorToast({
                message: error.message,
              });
            else {
              showErrorToast({
                message: error.Errors[0].Message,
              });
            }
          }
        },
      },
    );
  }

  const handleCancelRenameAlert = () => {
    let proceduresList: Array<ProcedureBuilderListType> | undefined = [];
    let newProcedures: Array<ProcedureBuilderListType> | undefined = [];
    if (procedures) proceduresList = [...procedures];
    if (searchValue)
      newProcedures =
        proceduresList &&
        proceduresList.filter(item => {
          if (
            item.ProcedureName.toLowerCase().includes(
              searchValue?.toLowerCase(),
            )
          )
            return item;

          return false;
        });
    else newProcedures = [...proceduresList];
    if (newProcedures) setProcedures([...newProcedures]);
  };
  /*********RENAME PROCEDURE******************************************************************/

  const handleUndoProcedureContent = () => {
    const selected = procedureDetails?.Data.AccountProcedure;
    const accountId = selected?.AccountId || 0;
    const procedureId = selected?.ProcedureId || 0;
    confirmAlert({
      message:
        'This will revert the procedure to its last published version. Do you want to continue?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            undoProcedureContent(
              {
                accountId: accountId,
                procedureId: procedureId,
              },
              {
                onSuccess: () => {
                  //
                },
                onError: (error: ErrorModal) => {
                  showErrorToast({
                    message: error.Errors[0].Message,
                  });
                },
              },
            );
          },
          className: 'pr-btn-primary',
        },
        {
          label: 'No',
          onClick: () => {
            //
          },
        },
      ],
    });
  };

  function importProcedure() {
    toggleImportProcedureVisible();
  }

  function linkProcedure() {
    toggleLinkProcedureVisible();
  }
  function addProcedure() {
    let content = '';
    if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
      content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
        procedureDetails?.Data?.AccountProcedure?.AccountId
      }/Policies/'></head><body>${(
        window as any
      )?.tinymce?.activeEditor?.getContent()}</body></html>`;
    }
    let unsavedChanges = false;
    if (
      (window as any)?.tinymce?.activeEditor?.isDirty() ||
      (userTyped === true && procedureDetails?.Data && content)
    )
      unsavedChanges = content !== procedureDetails.Data.Content;
    if (!(window as any)?.tinymce?.activeEditor?.isDirty() || !unsavedChanges) {
      createProcedure(
        {
          AccountId: accountId,
          CID: accountInfo?.accountFormInputs?.CID,
          SID: accountInfo?.accountFormInputs?.SID,
          PID: accountInfo?.accountFormInputs?.PID,
          AccountName: accountInfo?.accountFormInputs?.Name,
        },
        {
          onSuccess: (result: any) => {
            if (result?.Data) {
              setSelectedProcedures([result?.Data?.ProcedureId]);
              setRenameProc({
                AccountId: result.Data.AccountId,
                ProcedureId: result.Data.ProcedureId,
                ProcedureName: result.Data.ProcedureName,
              });
            }
          },
        },
      );
    } else {
      cancelAutosaveCallbacks();
      confirmAlert({
        message: 'Unsaved changes are present do you want to save the changes?',
        buttons: [
          {
            label: 'Yes',
            onClick: () => {
              saveContent(() => {
                setUserTyped(false);
                setEditorChanges(false);
                createProcedure(
                  {
                    AccountId: accountId,
                    CID: accountInfo?.accountFormInputs?.CID,
                    SID: accountInfo?.accountFormInputs?.SID,
                    PID: accountInfo?.accountFormInputs?.PID,
                    AccountName: accountInfo?.accountFormInputs?.Name,
                  },
                  {
                    onSuccess: (result: any) => {
                      if (result?.Data) {
                        setSelectedProcedures([result?.Data?.ProcedureId]);
                        setRenameProc({
                          AccountId: result.Data.AccountId,
                          ProcedureId: result.Data.ProcedureId,
                          ProcedureName: result.Data.ProcedureName,
                        });
                      }
                    },
                  },
                );
              });
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'No',
            onClick: () => {
              createProcedure(
                {
                  AccountId: accountId,
                  CID: accountInfo?.accountFormInputs?.CID,
                  SID: accountInfo?.accountFormInputs?.SID,
                  PID: accountInfo?.accountFormInputs?.PID,
                  AccountName: accountInfo?.accountFormInputs?.Name,
                },
                {
                  onSuccess: (result: any) => {
                    if (result?.Data) {
                      setSelectedProcedures([result?.Data?.ProcedureId]);
                      setRenameProc({
                        AccountId: result.Data.AccountId,
                        ProcedureId: result.Data.ProcedureId,
                        ProcedureName: result.Data.ProcedureName,
                      });
                    }
                  },
                },
              );
            },
          },
        ],
      });
    }
  }

  const handleCleanProcedureTemplate = () => {
    let newContent = '';
    if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
      newContent = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
        procedureDetails?.Data?.AccountProcedure?.AccountId
      }/Policies/'></head><body>${(
        window as any
      )?.tinymce?.activeEditor?.getContent()}</body></html>`;
    }
    formatCleanProcedure(
      {
        accountId,
        content: newContent,
      },
      {
        onSuccess: (response: any) => {
          const isIframePresent = checkIfIframe(response.Data);
          let contentTemp: string;
          if (isIframePresent === true) {
            contentTemp = addParamsToIframeSrc(response.Data);
          } else contentTemp = response.Data;
          setContent(contentTemp);
        },
      },
    );
  };

  const handleResetPolicyMenu = () => {
    confirmAlert({
      message:
        'Warning! This will rebuild the Policies Menu using the titles of currently visible procedures with default formatting. Manual changes will be lost. Do you want to proceed? ',
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            resetPolicyProcedure(
              {
                accountId: procedureDetails?.Data?.AccountProcedure?.AccountId,
                procedureId:
                  procedureDetails?.Data?.AccountProcedure?.ProcedureId,
              },
              {
                onSuccess: (response: any) => {
                  const isIframePresent = checkIfIframe(response.Data);
                  let contentTemp: string;
                  if (isIframePresent === true) {
                    contentTemp = addParamsToIframeSrc(response.Data);
                  } else contentTemp = response.Data;
                  setContent(contentTemp);
                  autoPublishPolicyContent();
                  // () =>
                  // procedures.forEach(item => {
                  //   if (
                  //     item.ProcedureId ===
                  //     procedureDetails?.Data?.AccountProcedure?.ProcedureId
                  //   ) {
                  //     item.IsPublished = false;
                  //   }
                  //   return item;
                  // }),
                  // );
                },
              },
            );
          },

          className: 'pr-btn-primary',
        },
        {
          label: 'No',
          onClick: () => {
            //
          },
        },
      ],
    });
  };

  const checkIsDiscardChanges = () => {
    const selectedProcedure = procedureDetails?.Data?.AccountProcedure;
    if (selectedProcedure?.Procedure?.RecentVersion === null) {
      return false;
    } else {
      if (selectedProcedure) {
        if (
          new Date(selectedProcedure?.Procedure?.LastUpdatedOn) >
          new Date(selectedProcedure?.Procedure?.RecentVersion?.CreatedOn)
        ) {
          return true;
        }
      }
    }
  };

  usePrompt(
    unsavedChanges,
    'Are you sure you want to leave? Changes that you made may not be saved.',
  );

  const brokenLinksInProcedure = useMemo(() => {
    if (
      brokenLinks &&
      brokenLinks?.length > 0 &&
      procedureDetails?.Data?.AccountProcedure?.ProcedureId
    ) {
      const brokenLinksArray: any = [];
      brokenLinks.forEach((item: any) => {
        if (
          item.ProcedureId ===
          procedureDetails?.Data?.AccountProcedure?.ProcedureId
        ) {
          item.ProcedureLinks.forEach((links: any): any => {
            brokenLinksArray.push(links.Link);
          });
        }
      });
      return brokenLinksArray;
    }
  }, [brokenLinks, procedureDetails?.Data?.AccountProcedure?.ProcedureId]);

  const selectedProcedureName = useMemo(() => {
    const selectedId = selectedProcedures?.[0];

    if (!procedureList?.Data || !selectedId) return undefined;

    return procedureList.Data.find(
      (item: any) => item.ProcedureId === selectedId,
    )?.ProcedureName;
  }, [procedureList, selectedProcedures]);

  const procedureIframeRef = useRef<HTMLIFrameElement>(null);

  const ddItems = [
    {
      id: 1,
      value: 'Crisis & Emergency',
      colorCode: '#d31e21ff',
      pattern:
        '<svg width="100%" height="100%"><pattern id="pattern-diagonal" width="5" height="5" patternUnits="userSpaceOnUse"><line x1="-1" y1="6" x2="6" y2="-1" stroke="currentColor" stroke-width="2" stroke-linecap="square" /><line x1="-6" y1="6" x2="1" y2="-1" stroke="currentColor" stroke-width="2" stroke-linecap="square" /><line x1="4" y1="6" x2="11" y2="-1" stroke="currentColor" stroke-width="2" stroke-linecap="square" /></pattern><rect x="0" y="0" width="100%" height="100%" fill="url(#pattern-diagonal)" /></svg>',
    },
    { id: 2, value: 'Clinical Support & Counselling', colorCode: '#4981f8ff' },
    { id: 3, value: 'Information & Referral', colorCode: '#25eb71ff' },
    {
      id: 4,
      value: 'Mobile Crisis & Specialization Dispatch',
      colorCode: '#c337f2ff',
    },
    {
      id: 5,
      value: 'Special Populations/Caller Types',
      colorCode: '#f08d2aff',
    },
    { id: 6, value: 'Administrative & Non-Clinical', colorCode: '#f9e378ff' },
    { id: 7, value: 'Law Enforcement & Public Safety', colorCode: '#dad7d7ff' },
    { id: 8, value: 'On-Call/Escalation', colorCode: '#573f3fff' },
  ];

  const getCurrentSvg = () => {
    if (!procedureCategories?.Data) {
      return getDefaultSvg();
    }

    const selectedCount = selectedProcedures?.length ?? 0;

    if (selectedCount === 0) {
      return getDefaultSvg();
    }

    const categoryId =
      selectedCount === 1
        ? procedureDetails?.Data?.AccountProcedure?.ProcedureCategoryId
        : getMultiProcedureCategoryId();

    if (!categoryId) {
      return getDefaultSvg();
    }

    const category = procedureCategories.Data.find(
      (cat: ProcedureCategoryType) => cat.Id === categoryId,
    );

    return category ? getCategoryIcon(category) : getDefaultSvg();
  };

  const getMultiProcedureCategoryId = () => {
    const filteredProcedures = procedureList?.Data?.filter((p: any) =>
      selectedProcedures.includes(p.ProcedureId),
    );

    if (!filteredProcedures?.length) return null;

    const allSameCategory = filteredProcedures.every(
      (p: any) =>
        p.ProcedureCategoryId === filteredProcedures[0].ProcedureCategoryId,
    );

    return allSameCategory ? filteredProcedures[0].ProcedureCategoryId : null;
  };

  const getCategoryIcon = (category: ProcedureCategoryType) =>
    `<span class="pcs-procedure-category__pattern_alias" style="--category-base: ${
      category.HexCode ?? '#fff'
    }">
    ${category.Pattern ?? ''}
  </span>`;

  const getDefaultSvg = () =>
    `<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="12" style="fill: var(--color-neutral-contrast-05)" />
  </svg>`;

  const listItemDropdown = (items: any) => (
    // {items.map}
    <button
      style={{
        display: 'flex',
        gap: '10px',
        border: 'none',
        background: '#e9eaeaff',
        cursor: 'pointer',
        padding: '5px',
        borderRadius: '5px',
      }}
    >
      <div
        style={{ display: 'flex' }}
        dangerouslySetInnerHTML={{
          __html: selectedProcedures ? (getCurrentSvg() as string) : ``,
        }}
      />
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="#656565ff"
        style={selectedProcedures ? { width: '16px' } : {}}
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="m19.5 8.25-7.5 7.5-7.5-7.5"
        />
      </svg>
    </button>
  );

  // const ListItemDropdown = ({
  //   items,
  // }: // onItemClick,
  // {
  //   items: any;
  //   // onItemClick?: (id: number) => void;
  // }) => {
  //   return (
  //     <ul className="dropdownMenuList">
  //       {items.map((item: any) => {
  //         return (
  //           <li key={item.id}>
  //             <button
  //               type="button"
  //               onClick={e => {
  //                 e.stopPropagation();
  //                 // onItemClick?.(item.Id);
  //               }}
  //               style={{
  //                 display: 'flex',
  //                 alignItems: 'center',
  //                 gap: '10px',
  //                 padding: '7px',
  //                 width: '100%',
  //               }}
  //             >
  //               {/* SVG Pattern */}
  //               <span
  //                 style={{
  //                   width: 18,
  //                   height: 18,
  //                   color: item.HexCode, // currentColor → OKLCH
  //                   flexShrink: 0,
  //                 }}
  //                 dangerouslySetInnerHTML={{
  //                   __html: item.pattern.replace(
  //                     /pattern-([a-z-]+)/g,
  //                     `pattern-$1-${item.id}`,
  //                   ),
  //                 }}
  //               />

  //               {/* Label */}
  //               {/* <span>{item.Label}</span> */}
  //             </button>
  //           </li>
  //         );
  //       })}
  //     </ul>
  //   );
  // };

  return (
    <MainContent flexDirection="column" m={-4} {...StyleProps}>
      <PageHeader
        title="Procedures"
        actions={pageActions(isProcedureCategoryUpdateInProgress)}
        subTitle={subtitle}
        SubtitleTootip={subtitle}
        className="procedureBuilder-header"
      />
      <Flex flex="1 1 auto" p={3}>
        <ProceduresLists
          brokenLinks={brokenLinks}
          accountId={accountId}
          totalBrokenLinksCount={totalBrokenLinksCount}
          procedureList={[]}
          isProcedureLoading={isLoading}
          procedures={procedures}
          setProcedures={setProcedures}
          selectedProcedures={selectedProcedures}
          setSelectedProcedures={setSelectedProcedures}
          updateProcedureVisibility={updateProcedureVisibility}
          renameProc={renameProc}
          setRenameProc={setRenameProc}
          handleRenameProcedures={handleRenameProcedures}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          selectProcedure={selectProcedure}
          appliedFilterOption={appliedFilterOption}
          setAppliedFilterOption={setAppliedFilterOption}
          addProcedure={addProcedure}
          importProcedure={importProcedure}
          linkProcedure={linkProcedure}
          toggleFileBrowser={toggleFileBrowser}
          setRenameFailed={setRenameFailed}
          renameFailed={renameFailed}
          autoSaveContent={autoSaveContent}
          procedureDetails={procedureDetails?.Data}
          setUserTyped={setUserTyped}
          setEditorChanges={setEditorChanges}
          saveContent={saveContent}
          userTyped={userTyped}
          editorChanges={editorChanges}
          procedureCategories={procedureCategories?.Data}
        />

        {selectedProcedures && selectedProcedures?.length === 1 && (
          <Card flex="1" ml={3} position="relative" procedureEditor>
            <CardHeader
              py={1}
              px={3}
              minHeight="38px"
              bg="var(--color-neutral-contrast-01)"
              alignItems="center"
              display="flex"
              justifyContent="space-between"
            >
              <Box
                minWidth="0"
                flex={2}
                style={{ display: 'flex', gap: '10px' }}
              >
                <Text
                  as="h4"
                  fontWeight="medium"
                  lineHeight={2}
                  color="text.body"
                  title={selectedProcedureName}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selectedProcedureName}
                </Text>
                {selectedProcedureName !== '0000_POLICIESMENU.html' && (
                  <>
                    <Dropdown
                      menuList={categoryMenuList}
                      trigger={listItemDropdown(categoryMenuList)}
                      position="right"
                      categoryMenu={true}
                      disabled={isProcedureCategoryUpdateInProgress}
                      //disabled={procedureDetails?.Data?.AccountProcedure?.Procedure?.IsGlobal || procedureDetails?.Data?.AccountProcedure?.Procedure?.IsLinked}
                      onButtonClick={categoryId => {
                        if (categoryId !== null && categoryId !== undefined) {
                          setSelectedCategoryId(categoryId);
                          updateProcedureCategory(
                            {
                              accountId: accountId,
                              procedureCategoryId: categoryId,
                              procedureIds: selectedProcedures || [],
                            },
                            {
                              onSuccess: () => {
                                showSuccessToast({
                                  message:
                                    'Procedure Category was updated successfully.',
                                });
                              },
                              onError: (error: any) => {
                                showErrorToast({
                                  message: error?.Errors[0].Message,
                                  autoClose: false,
                                });
                              },
                            },
                          );
                        }
                      }}
                    />
                    {isProcedureCategoryUpdateInProgress && (
                      <Badge
                        variant="warning"
                        style={{
                          alignSelf: 'center',
                          alignItems: 'center',
                          display: 'flex',
                          gap: '5px',
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          className="rotating"
                          style={{ width: '16px', height: '16px' }}
                        >
                          <path
                            fillRule="evenodd"
                            d="M13.836 2.477a.75.75 0 0 1 .75.75v3.182a.75.75 0 0 1-.75.75h-3.182a.75.75 0 0 1 0-1.5h1.37l-.84-.841a4.5 4.5 0 0 0-7.08.932.75.75 0 0 1-1.3-.75 6 6 0 0 1 9.44-1.242l.842.84V3.227a.75.75 0 0 1 .75-.75Zm-.911 7.5A.75.75 0 0 1 13.199 11a6 6 0 0 1-9.44 1.241l-.84-.84v1.371a.75.75 0 0 1-1.5 0V9.591a.75.75 0 0 1 .75-.75H5.35a.75.75 0 0 1 0 1.5H3.98l.841.841a4.5 4.5 0 0 0 7.08-.932.75.75 0 0 1 1.025-.273Z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Category updation is in progress.
                      </Badge>
                    )}
                  </>
                )}
              </Box>
              <Flex mx={3} flex="1 0 auto" justifyContent="center">
                <ButtonGroup>
                  <Button
                    variant={mode === 'Edit' ? 'secondary' : undefined}
                    size="sm"
                    onClick={() => setMode('Edit')}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant={mode === 'Preview' ? 'secondary' : undefined}
                    onClick={() => {
                      setMode('Preview');
                      let content = '';
                      const embedStyle =
                        '<style>#navcontainer{width:100%}#navcontainer ul{margin-left:10px;padding-left:0;list-style-type:none;font-family:"Times New Roman",Times,serif;font-size:16px;font-weight:700}#navcontainer a{display:block;padding:2px;width:100%;background-color:#add8e6;border-bottom:1px solid #eee}#navcontainer a:link,#navlist a:visited{color:#000;text-decoration:none}#navcontainer a:hover{background-color:Orange;color:#fff}</style>';
                      if (
                        (window as any)?.tinymce?.activeEditor?.initialized ===
                        true
                      ) {
                        content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
                          procedureDetails?.Data?.AccountProcedure?.AccountId
                        }/Policies/'>${
                          procedureDetails?.Data?.AccountProcedure?.Procedure
                            ?.Name === '0000_POLICIESMENU.html'
                            ? embedStyle
                            : ''
                        }</head><body>${(
                          window as any
                        )?.tinymce?.activeEditor?.getContent()}</body></html>`;
                      }
                      const isIframePresent = checkIfIframe(content);
                      let contentTemp: string;
                      if (isIframePresent === true) {
                        contentTemp = addParamsToIframeSrc(content);
                      } else contentTemp = content;
                      setContent(contentTemp);
                    }}
                  >
                    Preview
                  </Button>
                </ButtonGroup>
              </Flex>

              <Flex flex={2.5} alignItems="center" justifyContent={'flex-end'}>
                {lastEditedPerson && lastEditedTime ? (
                  <Flex style={{ gap: '0.5rem' }}>
                    <LastEdited
                      onClick={() => {
                        toggleVersionHistory();
                      }}
                    >
                      <small>
                        Last edit was made on{' '}
                        <strong>{`${lastEditedTime.date} at ${lastEditedTime.time}`}</strong>{' '}
                        {lastEditedPerson === 'Auto Generated' ? '-' : 'by'}{' '}
                        <strong> {lastEditedPerson}</strong>
                      </small>
                    </LastEdited>
                    <VersionButton
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        toggleVersionHistory();
                      }}
                    >
                      Revision History
                    </VersionButton>
                  </Flex>
                ) : (
                  <Flex style={{ gap: '0.5rem' }}>
                    <InfoText>
                      <Icon.ExclamationCircle size={'1rem'} title="" />
                      <small>
                        Procedure has never been published and is not yet
                        available to users
                      </small>
                    </InfoText>
                    <VersionButton variant="outline" size="sm" disabled>
                      Revision History
                    </VersionButton>
                  </Flex>
                )}
              </Flex>
            </CardHeader>
            <Flex flex={1}>
              <ProcedureEditActions>
                <Tooltip
                  animation="fade"
                  title="Save"
                  position="right"
                  arrow={true}
                >
                  <button
                    onClick={() => {
                      cancelAutosaveCallbacks();
                      saveContent();
                    }}
                    disabled={
                      mode === 'Edit' ||
                      procedureDetails?.Data?.AccountProcedure?.Procedure
                        ?.Name === '0000_POLICIESMENU.html'
                        ? false
                        : true
                    }
                  >
                    <Icon.Save />
                  </button>
                </Tooltip>
                {selectedProcedureName === '0000_POLICIESMENU.html' ? null : (
                  <Tooltip
                    animation="fade"
                    title="Discard Changes"
                    position="right"
                    arrow={true}
                  >
                    <button
                      disabled={
                        checkIsDiscardChanges() && mode === 'Edit'
                          ? false
                          : true
                      }
                      onClick={handleUndoProcedureContent}
                    >
                      <Icon.Backspace />
                    </button>
                  </Tooltip>
                )}
                <Tooltip
                  animation="fade"
                  title="Save Layout"
                  position="right"
                  arrow={true}
                >
                  <button
                    disabled={mode === 'Edit' ? false : true}
                    onClick={() => {
                      const htmlContentBackup = (
                        window as any
                      )?.tinymce?.activeEditor?.getContent();
                      const html = (
                        window as any
                      )?.tinymce?.activeEditor?.getBody();
                      // const updated: Array<string> = [];
                      const replaceText = (elem: Element) => {
                        for (const child of Array.from(elem.children)) {
                          if (child?.children?.length > 0) {
                            replaceText(child);
                          } else {
                            if (child.textContent) {
                              child.textContent = 'Enter your text here';
                            }
                          }
                        }
                      };
                      replaceText(html);
                      const innerHTML = html.innerHTML;
                      toggleSaveLayout(innerHTML);
                      (window as any)?.tinymce?.activeEditor?.setContent(
                        htmlContentBackup,
                      );
                      //
                    }}
                  >
                    <Icon.Layout />
                  </button>
                </Tooltip>
                <Tooltip
                  animation="fade"
                  title="Save Table"
                  position="right"
                  arrow={true}
                >
                  <button
                    disabled={mode === 'Edit' && isTableSelected ? false : true}
                    onClick={() => {
                      const htmlContentBackup = (
                        window as any
                      )?.tinymce?.activeEditor?.getContent();
                      const html = (
                        window as any
                      )?.tinymce?.activeEditor?.selection
                        ?.getNode()
                        ?.closest('table');
                      const htmlString = html?.outerHTML;
                      toggleSaveTable(htmlString);
                      (window as any)?.tinymce?.activeEditor?.setContent(
                        htmlContentBackup,
                      );
                    }}
                  >
                    <Icon.LgTable />
                  </button>
                </Tooltip>
                {/* print for both edit and preview */}
                <Tooltip
                  animation="fade"
                  title="Print"
                  position="right"
                  arrow={true}
                >
                  <button
                    onClick={() =>
                      (window as any)?.tinymce?.activeEditor?.execCommand(
                        'mcePrint',
                      )
                    }
                  >
                    <Icon.Print />
                  </button>
                </Tooltip>
                {selectedProcedureName === '0000_POLICIESMENU.html' ? (
                  <Tooltip
                    animation="fade"
                    title="Reset Policy Menu"
                    position="right"
                    arrow={true}
                  >
                    <button onClick={() => handleResetPolicyMenu()}>
                      <Icon.Refresh title="" />
                    </button>
                  </Tooltip>
                ) : null}
              </ProcedureEditActions>
              <ProcedureEditorWrapper>
                {mode === 'Edit' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '18px',
                      right: '20px',
                      zIndex: 10,
                    }}
                  >
                    <Checkbox
                      disabled={
                        procedureDetails?.Data?.AccountProcedure?.Procedure
                          ?.Name === '0000_POLICIESMENU.html'
                          ? true
                          : false
                      }
                      checked={
                        procedureDetails?.Data?.AccountProcedure?.Procedure
                          ?.Name === '0000_POLICIESMENU.html'
                          ? true
                          : procedureDetails?.Data?.AccountProcedure?.Procedure
                              ?.IsAutoSave
                      }
                      label="Auto Save"
                      name="autosave"
                      handleChange={() => {
                        let content = '';
                        if (
                          (window as any)?.tinymce?.activeEditor
                            ?.initialized === true
                        ) {
                          content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
                            procedureDetails?.Data?.AccountProcedure?.AccountId
                          }/Policies/'></head><body>${(
                            window as any
                          )?.tinymce?.activeEditor?.getContent()}</body></html>`;
                        }
                        let unsavedChanges = false;
                        if (
                          userTyped === true &&
                          procedureDetails?.Data &&
                          content
                        )
                          unsavedChanges =
                            content !== procedureDetails.Data.Content;
                        if (
                          (window as any)?.tinymce?.activeEditor?.isDirty() ||
                          unsavedChanges
                        ) {
                          cancelAutosaveCallbacks();
                          confirmAlert({
                            message:
                              'Unsaved changes are present do you want to save the changes?',
                            buttons: [
                              {
                                label: 'Yes',
                                onClick: () => {
                                  saveContent(() => {
                                    setUserTyped(false);
                                    setEditorChanges(false);
                                    if (procedureDetails?.Data) {
                                      updateProcedureAutoSaveOption({
                                        AccountId:
                                          procedureDetails?.Data
                                            ?.AccountProcedure?.AccountId,
                                        Procedure: {
                                          ProcedureId:
                                            procedureDetails.Data
                                              .AccountProcedure.Procedure.Id,
                                          IsAutoSave:
                                            !procedureDetails.Data
                                              .AccountProcedure.Procedure
                                              .IsAutoSave,
                                        },
                                      });
                                    }
                                  });
                                },
                                className: 'pr-btn-primary',
                              },
                              {
                                label: 'No',
                                onClick: () => {
                                  if (procedureDetails?.Data) {
                                    updateProcedureAutoSaveOption({
                                      AccountId:
                                        procedureDetails?.Data?.AccountProcedure
                                          ?.AccountId,
                                      Procedure: {
                                        ProcedureId:
                                          procedureDetails.Data.AccountProcedure
                                            .Procedure.Id,
                                        IsAutoSave:
                                          !procedureDetails.Data
                                            .AccountProcedure.Procedure
                                            .IsAutoSave,
                                      },
                                    });
                                  }
                                },
                              },
                            ],
                          });
                        } else {
                          if (procedureDetails?.Data) {
                            updateProcedureAutoSaveOption({
                              AccountId:
                                procedureDetails.Data.AccountProcedure
                                  .AccountId,
                              Procedure: {
                                ProcedureId:
                                  procedureDetails.Data.AccountProcedure
                                    .Procedure.Id,
                                IsAutoSave:
                                  !procedureDetails.Data.AccountProcedure
                                    .Procedure.IsAutoSave,
                              },
                            });
                          }
                        }
                      }}
                    />
                  </Box>
                )}

                {mode === 'Edit' && !isLoading && (
                  <TinyMCE
                    // key={procedureDetails?.Data?.AccountProcedure?.ProcedureId}
                    colorScheme={colorScheme}
                    setEditorChanges={setEditorChanges}
                    height={695}
                    value={content}
                    brokenLinks={brokenLinksInProcedure}
                    name="ProcedureContent"
                    disabled={
                      procedureDetails?.Data?.AccountProcedure?.Procedure
                        ?.Name === '0000_POLICIESMENU.html'
                        ? true
                        : false
                    }
                    onUserTyped={
                      procedureDetails?.Data?.AccountProcedure?.Procedure
                        ?.Name === '0000_POLICIESMENU.html'
                        ? () => {
                            // if (
                            //   stripAndTrimHTML(
                            //     procedureDetails?.Data?.Content,
                            //   ) !== '' &&
                            //   stripAndTrimHTML(
                            //     (
                            //       window as any
                            //     )?.tinymce?.activeEditor?.getContent(),
                            //   ) === ''
                            // ) {
                            //   confirmAlert({
                            //     message:
                            //       'The Policy menu cannot be left empty. Your last saved entries will be restored.',
                            //     closeOnClickOutside: false,
                            //     buttons: [
                            //       {
                            //         label: 'Ok',
                            //         onClick: async () => {
                            //           const result =
                            //             await refetchProcedureDetails();
                            //           let content =
                            //             result?.data?.Data?.Content ?? '';
                            //           (
                            //             window as any
                            //           )?.tinymce?.activeEditor?.setContent(
                            //             content,
                            //           );
                            //         },
                            //         className: 'pr-btn-primary',
                            //       },
                            //     ],
                            //   });
                            // }
                            setUserTyped(true);
                            lazyRun(() => {
                              if (
                                autoPublishPolicyContent &&
                                !isPolicyMenuResetting
                              )
                                autoPublishPolicyContent(() => {
                                  //
                                });
                            }, 7000);
                          }
                        : (undo?: boolean) => {
                            setUserTyped(true);
                            lazyRun(() => {
                              if (
                                autoSaveContent &&
                                procedureDetails?.Data?.AccountProcedure
                                  ?.Procedure.IsAutoSave
                              )
                                autoSaveContent(() => {
                                  setUserTyped(false);
                                  setEditorChanges(false);
                                }, undo);
                            }, 7000);
                          }
                    }
                    onDetectTableSelection={() => {
                      const isTablePresent = (
                        window as any
                      )?.tinymce?.activeEditor?.selection
                        ?.getNode()
                        ?.closest('table');
                      if (isTablePresent) setIsTableSelected(true);
                      else setIsTableSelected(false);
                    }}
                    toggleEmbeddedProcedure={toggleEmbeddedProcedure}
                    toggleExistingLayout={toggleExistingLayout}
                    toggleExistingTable={toggleExistingTable}
                    toggleFileImage={toggleFileImage}
                    toggleHyperlink={toggleHyperlink}
                    procedureId={
                      procedureDetails?.Data?.AccountProcedure?.ProcedureId
                    }
                    handleCleanProcedureTemplate={handleCleanProcedureTemplate}
                  />
                )}

                {mode === 'Preview' && !isLoading && (
                  <Stack direction="y" gap={3} height="100%">
                    {procedureDetails?.Data?.AccountProcedure.Procedure.Name ===
                      '0000_POLICIESMENU.html' && (
                      <div style={{ width: '30%' }}>
                        <Button
                          variant="primary"
                          onClick={() => {
                            setRandom(random => random + 1);
                          }}
                          type="submit"
                          fullWidth={false}
                        >
                          Home
                        </Button>
                      </div>
                    )}

                    <IframeWrapper>
                      {!iframeLoader ? <Loader /> : null}

                      <Iframe
                        className={!iframeLoader ? 'hide' : 'narrow'}
                        ref={procedureIframeRef}
                        height="100%"
                        key={random}
                        onLoad={() => setIframeLoader(true)}
                        src={`${apiBaseUrl}procedures/V1/accountfolders/${procedureDetails?.Data?.AccountProcedure?.AccountId}/Policies/${procedureDetails?.Data?.AccountProcedure.Procedure.Name}?procedureDisplayType=3`}
                      />
                    </IframeWrapper>
                    <Flex style={{ gap: `0.5rem` }} justifyContent="flex-end">
                      <Alert
                        variant="info"
                        icon={<Icon.ExclamationCircle />}
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '3px',
                        }}
                      >
                        <Flex
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text color="currentColor">
                            Please click the{' '}
                            <strong>procedure refresh button</strong> if the
                            procedure is not loaded properly.
                          </Text>
                        </Flex>
                      </Alert>
                      <Button
                        variant="primary"
                        onClick={() => {
                          setRandom(random => random + 1);
                        }}
                        type="submit"
                        fullWidth={false}
                      >
                        <Icon.Refresh title="" />
                      </Button>
                    </Flex>
                  </Stack>
                )}
              </ProcedureEditorWrapper>
            </Flex>
          </Card>
        )}
        {selectedProcedures && selectedProcedures?.length > 1 && (
          <SelectedProceduresList
            listItemDropdown={listItemDropdown}
            categoryMenuList={categoryMenuList}
            updateProcedureCategory={updateProcedureCategory}
            selectedProcedures={selectedProcedures}
            setSelectedProcedures={setSelectedProcedures}
            procedureList={procedureList?.Data}
            isProcedureCategoryUpdateInProgress={
              isProcedureCategoryUpdateInProgress
            }
          />
        )}
        {selectedProcedures && selectedProcedures?.length === 0 ? (
          <Card
            flex="1"
            ml={3}
            position="relative"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <EmptyState>
              <NoDataImage feedback="Please Select an existing Procedure or Create/Import a New Procedure in order to Manage it." />
            </EmptyState>
          </Card>
        ) : null}
      </Flex>
      {showEmbeddedProcedure && (
        <EmbeddedProcedure
          accountId={accountId}
          showPanel={showEmbeddedProcedure}
          togglePanel={toggleEmbeddedProcedure}
        />
      )}

      {showExistingLayout && (
        <ExistingLayout
          accountId={accountId}
          showPanel={showExistingLayout}
          togglePanel={toggleExistingLayout}
        />
      )}

      {showExistingTable && (
        <ExistingTable
          accountId={accountId}
          showPanel={showExistingTable}
          togglePanel={toggleExistingTable}
        />
      )}
      {showFileImage && (
        <FileImage
          accountId={accountId}
          account={`${accountInfo?.accountInfoReturn?.Data.CID}${'-'}${
            accountInfo?.accountInfoReturn?.Data.SID
          }${'-'}${accountInfo?.accountInfoReturn?.Data.PID}`}
          showPanel={showFileImage}
          togglePanel={toggleFileImage}
          createFolder={createFolder}
          uploadFile={uploadFile}
          deleteFile={deleteFile}
          addFileToEditor={(file: BlobType) => {
            if (file.Type !== 'image') {
              const hrefTag = `<a href="${apiBaseUrl}accounts/procedures/get-external-file?filepath=root/${file.Path}">${file.Name}</a>`;
              (window as any)?.tinymce?.activeEditor?.insertContent(hrefTag);
            } else {
              const hrefTag = `<img src="${file.Uri}" />`;
              (window as any)?.tinymce?.activeEditor?.insertContent(hrefTag);
            }
          }}
        />
      )}

      {!!showHyperlink && (
        <Hyperlink
          accountId={accountId}
          showPanel={!!showHyperlink}
          togglePanel={() => setShowHyperlink('')}
          selectedWords={showHyperlink}
          tagsList={getAllTags()}
        />
      )}

      {importProcedureVisible && (
        <ImportProcedure
          togglePanel={toggleImportProcedureVisible}
          showPanel={importProcedureVisible}
        />
      )}

      {linkProcedureVisible && (
        <LinkProcedure
          togglePanel={toggleLinkProcedureVisible}
          showPanel={linkProcedureVisible}
          accountInfo={{
            cid: accountInfo?.accountInfoReturn?.Data.CID,
            pid: accountInfo?.accountInfoReturn?.Data.PID,
            sid: accountInfo?.accountInfoReturn?.Data.SID,
            accountName: accountInfo?.accountInfoReturn?.Data.Name,
          }}
        />
      )}

      {versionHistoryVisible && selectedProcedures && selectedProcedures[0] ? (
        <VersionHistory
          togglePanel={toggleVersionHistory}
          showPanel={versionHistoryVisible}
          procedureId={selectedProcedures[0]}
          fileId={procedureDetails?.Data?.AccountProcedure?.Procedure?.FileId}
          accountInfo={{
            cid: accountInfo?.accountInfoReturn?.Data.CID,
            pid: accountInfo?.accountInfoReturn?.Data.PID,
            sid: accountInfo?.accountInfoReturn?.Data.SID,
          }}
        />
      ) : null}

      {fileBrowserVisible && (
        <FileBrowser
          togglePanel={toggleFileBrowser}
          showPanel={fileBrowserVisible}
          account={`${accountInfo?.accountInfoReturn?.Data.CID}${'-'}${
            accountInfo?.accountInfoReturn?.Data.SID
          }${'-'}${accountInfo?.accountInfoReturn?.Data.PID}`}
        />
      )}

      {showSaveLayout && (
        <SaveLayout
          showPanel={!!showSaveLayout}
          accountId={accountId}
          togglePanel={() => toggleSaveLayout()}
          onSavePress={(title: string, saveOption: boolean) => {
            setIsSavingLayout(true);
            autoSaveContent(() => {
              setUserTyped(false);
              setEditorChanges(false);
            });
            //const specialChars = /[\\/]/;
            const validationMessage = checkAzureFileNameValidation(title);
            if (!title || !title.trim() || validationMessage !== 'Valid') {
              if (saveOption) showErrorToast({ message: validationMessage });
              else showErrorToast({ message: validationMessage });
              return;
            }
            if (saveOption) {
              saveTemplate(
                {
                  params: {
                    Name: title.trim(),
                    Content: showSaveLayout || '',
                    ContainerName: 'layouts',
                  },
                  accountId: accountId,
                },
                {
                  onSuccess: () => {
                    toggleSaveLayout();
                    showSuccessToast({ message: 'Layout saved successfully' });
                    setIsSavingLayout(false);
                  },
                  onError: () => {
                    showErrorToast({
                      message:
                        'Couldn’t save layout. Please try after some time.',
                      autoClose: false,
                    });
                    setIsSavingLayout(false);
                  },
                },
              );
            } else {
              updateTemplate(
                {
                  params: {
                    Name: title.trim(),
                    Content: showSaveLayout || '',
                    ContainerName: 'layouts',
                  },
                  accountId: accountId,
                },
                {
                  onSuccess: () => {
                    toggleSaveLayout();
                    showSuccessToast({ message: 'Layout saved successfully' });
                    setIsSavingLayout(false);
                  },
                  onError: () => {
                    showErrorToast({
                      message:
                        'Couldn’t save layout. Please try after some time.',
                      autoClose: false,
                    });
                    setIsSavingLayout(false);
                  },
                },
              );
            }
          }}
        />
      )}

      {showSaveTable && (
        <SaveTable
          showPanel={!!showSaveTable}
          accountId={accountId}
          togglePanel={() => toggleSaveTable()}
          onSavePress={(title: string, saveOption: boolean) => {
            setIsSavingLayout(true);
            autoSaveContent(() => {
              setUserTyped(false);
              setEditorChanges(false);
            });
            //const specialChars = /[\\/]/;
            const validationMessage = checkAzureFileNameValidation(title);
            if (!title || !title.trim() || validationMessage !== 'Valid') {
              if (saveOption) showErrorToast({ message: validationMessage });
              else showErrorToast({ message: validationMessage });
              return;
            }
            if (saveOption) {
              saveTemplate(
                {
                  params: {
                    Name: title.trim(),
                    Content: showSaveTable || '',
                    ContainerName: 'tables',
                  },
                  accountId: accountId,
                },
                {
                  onSuccess: () => {
                    toggleSaveTable();
                    showSuccessToast({ message: 'Table saved successfully' });
                    setIsSavingLayout(false);
                  },
                  onError: () => {
                    showErrorToast({
                      message:
                        'Couldn’t save table. Please try after some time.',
                      autoClose: false,
                    });
                    setIsSavingLayout(false);
                  },
                },
              );
            } else {
              updateTemplate(
                {
                  params: {
                    Name: title.trim(),
                    Content: showSaveTable || '',
                    ContainerName: 'tables',
                  },
                  accountId: accountId,
                },
                {
                  onSuccess: () => {
                    toggleSaveTable();
                    showSuccessToast({ message: 'Table saved successfully' });
                    setIsSavingLayout(false);
                  },
                  onError: () => {
                    showErrorToast({
                      message:
                        'Couldn’t save table. Please try after some time.',
                      autoClose: false,
                    });
                    setIsSavingLayout(false);
                  },
                },
              );
            }
          }}
        />
      )}

      {showBrokenLinks && (
        <BrokenLinks
          showPanel={showBrokenLinks}
          togglePanel={() => toggleBrokenLinks()}
          accountId={accountId}
          selectedProcedures={selectedProcedures}
          autoSaveContent={autoSaveContent}
        />
      )}
      {(isLoading ||
        isPolicyMenuResetting ||
        isSaveProcedureLoading ||
        isPolicyMenuCreating) && <Loader />}
    </MainContent>
  );
}
